/**
 * Currency Formatting Utility
 * Provides culture-specific number and currency formatting based on selected currency
 */

// Currency configuration with culture-specific settings
const CURRENCY_CONFIG = {
  USD: {
    locale: 'en-US',
    currency: 'USD',
    symbol: '$',
    name: 'US Dollar'
  },
  EUR: {
    locale: 'de-DE',
    currency: 'EUR', 
    symbol: '€',
    name: 'Euro'
  },
  GBP: {
    locale: 'en-GB',
    currency: 'GBP',
    symbol: '£',
    name: 'British Pound'
  },
  CRC: {
    locale: 'es-CR',
    currency: 'CRC',
    symbol: '₡',
    name: 'Costa Rican Colón'
  },
  MXN: {
    locale: 'es-MX',
    currency: 'MXN',
    symbol: '$',
    name: 'Mexican Peso'
  },
  CAD: {
    locale: 'en-CA',
    currency: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar'
  },
  AUD: {
    locale: 'en-AU',
    currency: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar'
  },
  JPY: {
    locale: 'ja-JP',
    currency: 'JPY',
    symbol: '¥',
    name: 'Japanese Yen'
  },
  BRL: {
    locale: 'pt-BR',
    currency: 'BRL',
    symbol: 'R$',
    name: 'Brazilian Real'
  },
  INR: {
    locale: 'en-IN',
    currency: 'INR',
    symbol: '₹',
    name: 'Indian Rupee'
  },
  COP: {
    locale: 'es-CO',
    currency: 'COP',
    symbol: '$',
    name: 'Colombian Peso'
  },
  PEN: {
    locale: 'es-PE',
    currency: 'PEN',
    symbol: 'S/',
    name: 'Peruvian Sol'
  },
  BTC: {
    locale: 'en-US',
    currency: 'BTC',
    symbol: '₿',
    name: 'Bitcoin'
  },
  SOLES: {
    locale: 'es-PE',
    currency: 'PEN',
    symbol: 'S/',
    name: 'Peruvian Soles'
  },
  CEC: {
    locale: 'en-US',
    currency: 'CEC',
    symbol: '¢',
    name: 'CEC Currency'
  }
};

/**
 * Format currency amount with culture-specific formatting
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR')
 * @param {boolean} showSymbol - Whether to show currency symbol (default: true)
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency, showSymbol = true) => {
  if (!amount && amount !== 0) return '$0.00';
  
  const config = CURRENCY_CONFIG[currency?.toUpperCase()];
  if (!config) {
    // Fallback to USD formatting if currency not found
    return new Intl.NumberFormat('en-US', {
      style: showSymbol ? 'currency' : 'decimal',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  try {
    if (showSymbol) {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } else {
      return new Intl.NumberFormat(config.locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    }
  } catch (error) {
    // Fallback formatting
    console.warn(`Currency formatting failed for ${currency}:`, error);
    return `${config.symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

/**
 * Format number with culture-specific formatting (no currency symbol)
 * @param {number} number - The number to format
 * @param {string} currency - The currency code for locale context
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} - Formatted number string
 */
export const formatNumber = (number, currency, decimals = 0) => {
  if (!number && number !== 0) return '0';
  
  const config = CURRENCY_CONFIG[currency?.toUpperCase()];
  const locale = config?.locale || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(number);
  } catch (error) {
    console.warn(`Number formatting failed for ${currency}:`, error);
    return number.toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  }
};

/**
 * Get currency symbol for a given currency code
 * @param {string} currency - The currency code
 * @returns {string} - Currency symbol
 */
export const getCurrencySymbol = (currency) => {
  const config = CURRENCY_CONFIG[currency?.toUpperCase()];
  return config?.symbol || '$';
};

/**
 * Check if specific currency is selected (not "All Currencies")
 * @param {string} currency - The currency filter value
 * @returns {boolean} - True if specific currency is selected
 */
export const isSpecificCurrency = (currency) => {
  return currency && currency !== 'all' && currency !== '';
};

/**
 * Format amount based on currency context
 * If specific currency is selected, use culture formatting
 * If "All Currencies" or no currency, use default USD formatting
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency filter value
 * @param {boolean} showSymbol - Whether to show currency symbol
 * @returns {string} - Formatted currency string
 */
export const formatContextualCurrency = (amount, currency, showSymbol = true) => {
  if (isSpecificCurrency(currency)) {
    return formatCurrency(amount, currency, showSymbol);
  } else {
    // Default formatting for "All Currencies"
    return showSymbol ? 
      `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` :
      amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
};

/**
 * Format count/number based on currency context
 * @param {number} count - The count to format
 * @param {string} currency - The currency filter value
 * @returns {string} - Formatted count string
 */
export const formatContextualNumber = (count, currency) => {
  if (isSpecificCurrency(currency)) {
    return formatNumber(count, currency);
  } else {
    return count.toLocaleString('en-US');
  }
};

// Currencies that use 0 decimals in Overview screen
const OVERVIEW_ZERO_DECIMAL_CURRENCIES = ['USD', 'MXN', 'COP', 'CRC', 'PEN'];

/**
 * Format currency amount for Overview screen
 * USD, MXN, COP, CRC, PEN use 0 decimals
 * All other currencies use 2 decimals
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code or filter value
 * @param {boolean} showSymbol - Whether to show currency symbol (default: true)
 * @returns {string} - Formatted currency string for Overview
 */
export const formatCurrencyForOverview = (amount, currency, showSymbol = true) => {
  if (!amount && amount !== 0) return '$0';
  
  // For "all currencies" case, use USD formatting with 0 decimals
  if (!isSpecificCurrency(currency)) {
    return showSymbol ? 
      `$${Math.round(amount).toLocaleString('en-US')}` :
      Math.round(amount).toLocaleString('en-US');
  }
  
  const currencyCode = currency.toUpperCase();
  const config = CURRENCY_CONFIG[currencyCode];
  
  if (!config) {
    // Fallback to USD formatting with 0 decimals
    return showSymbol ? `$${Math.round(amount).toLocaleString('en-US')}` : Math.round(amount).toLocaleString('en-US');
  }
  
  // Determine decimal places based on currency
  const decimals = OVERVIEW_ZERO_DECIMAL_CURRENCIES.includes(currencyCode) ? 0 : 2;
  
  try {
    if (showSymbol) {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(amount);
    } else {
      return new Intl.NumberFormat(config.locale, {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(amount);
    }
  } catch (error) {
    console.warn(`Overview currency formatting failed for ${currency}:`, error);
    const formattedAmount = decimals === 0 ? 
      Math.round(amount).toLocaleString('en-US') : 
      amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return showSymbol ? `${config.symbol}${formattedAmount}` : formattedAmount;
  }
};

/**
 * Format number for Overview screen (always 0 decimals)
 * @param {number} number - The number to format
 * @param {string} currency - The currency code for locale context
 * @returns {string} - Formatted number string with 0 decimals
 */
export const formatNumberForOverview = (number, currency) => {
  if (!number && number !== 0) return '0';
  
  const config = CURRENCY_CONFIG[currency?.toUpperCase()];
  const locale = config?.locale || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.round(number));
  } catch (error) {
    console.warn(`Overview number formatting failed for ${currency}:`, error);
    return Math.round(number).toLocaleString('en-US');
  }
};

/**
 * Format currency amount for other screens (always 2 decimals)
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code or filter value
 * @param {boolean} showSymbol - Whether to show currency symbol (default: true)
 * @returns {string} - Formatted currency string with 2 decimals
 */
export const formatCurrencyForOtherScreens = (amount, currency, showSymbol = true) => {
  if (!amount && amount !== 0) return '$0.00';
  
  // For "all currencies" case, use USD formatting with 2 decimals
  if (!isSpecificCurrency(currency)) {
    return showSymbol ? 
      `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` :
      amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  
  if (!config) {
    // Fallback to USD formatting with 2 decimals
    return showSymbol ? 
      `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` :
      amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  
  try {
    if (showSymbol) {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } else {
      return new Intl.NumberFormat(config.locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    }
  } catch (error) {
    console.warn(`Other screens currency formatting failed for ${currency}:`, error);
    const formattedAmount = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return showSymbol ? `${config.symbol}${formattedAmount}` : formattedAmount;
  }
};

/**
 * Format currency for display in dropdowns and selectors
 * Shows symbol with currency code when specific currency is selected
 * @param {string} currency - The currency code
 * @param {boolean} showCode - Whether to show currency code alongside symbol
 * @returns {string} - Formatted currency display (e.g., "₡ CRC" or "$ USD")
 */
export const formatCurrencySelector = (currency, showCode = true) => {
  if (!currency || currency === 'all') {
    return 'All Currencies';
  }
  
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  if (!config) {
    // Fallback for unknown currencies
    return showCode ? `$ ${currency.toUpperCase()}` : '$';
  }
  
  return showCode ? `${config.symbol} ${currency.toUpperCase()}` : config.symbol;
};

/**
 * Get currency display for selected value
 * Returns just the symbol for compact display
 * @param {string} currency - The currency code
 * @returns {string} - Currency symbol or fallback
 */
export const getCurrencyDisplay = (currency) => {
  if (!currency || currency === 'all') {
    return 'All Currencies';
  }
  
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  return config?.symbol || '$';
};

/**
 * Get currency name for tooltips or extended display
 * @param {string} currency - The currency code
 * @returns {string} - Full currency name
 */
export const getCurrencyName = (currency) => {
  if (!currency || currency === 'all') {
    return 'All Currencies';
  }
  
  const config = CURRENCY_CONFIG[currency.toUpperCase()];
  return config?.name || currency.toUpperCase();
};

/**
 * Get all supported currencies for dropdown options
 * @returns {Array} - Array of currency objects with code, symbol, and name
 */
export const getSupportedCurrencies = () => {
  return Object.entries(CURRENCY_CONFIG).map(([code, config]) => ({
    code,
    symbol: config.symbol,
    name: config.name,
    displayText: `${config.symbol} ${code}`,
    locale: config.locale
  }));
};

export default {
  formatCurrency,
  formatNumber,
  getCurrencySymbol,
  isSpecificCurrency,
  formatContextualCurrency,
  formatContextualNumber,
  formatCurrencyForOverview,
  formatNumberForOverview,
  formatCurrencyForOtherScreens,
  formatCurrencySelector,
  getCurrencyDisplay,
  getCurrencyName,
  getSupportedCurrencies,
  CURRENCY_CONFIG
};